import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PencilSimple, Check, X, ArrowCounterClockwise, Sparkle } from "@phosphor-icons/react";

/**
 * Inline-editable candidate field with field-source ownership badge.
 * Props:
 *  - candidateId: string
 *  - field: string (server field name)
 *  - label: string
 *  - value: current value
 *  - type: "text" | "textarea" | "list" | "date" | "number" | "select" | "email" | "tel" | "url"
 *  - options: for select
 *  - source: "manual" | "evaluation" | undefined
 *  - canEdit: boolean
 *  - onUpdated: (nextCandidate) => void
 */
export default function EditableField({
  candidateId, field, label, value, type = "text",
  options = [], source, canEdit = true, onUpdated, hint,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setDraft(value); setEditing(true); };
  const cancel = () => { setEditing(false); setDraft(value); };

  const save = async () => {
    setSaving(true);
    try {
      let payload = draft;
      if (type === "list") payload = (draft || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (type === "number") payload = draft === "" ? null : Number(draft);
      const r = await api.put(`/candidates/${candidateId}`, { [field]: payload });
      onUpdated?.(r.data);
      toast.success(`${label} updated`);
      setEditing(false);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const revert = async () => {
    if (!window.confirm(`Revert "${label}" to the value from the latest evaluation?`)) return;
    try {
      const r = await api.post(`/candidates/${candidateId}/revert-field/${field}`);
      onUpdated?.(r.data);
      toast.success(`${label} reverted to evaluation value`);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const displayValue = () => {
    if (value === null || value === undefined || value === "") return <span className="text-slate-300">—</span>;
    if (Array.isArray(value)) return value.join(", ") || <span className="text-slate-300">—</span>;
    return String(value);
  };

  const editInput = () => {
    const base = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm";
    if (type === "textarea")
      return <textarea rows={3} value={draft ?? ""} onChange={(e) => setDraft(e.target.value)}
                       data-testid={`edit-${field}-input`} className={base} />;
    if (type === "select")
      return (
        <select value={draft ?? ""} onChange={(e) => setDraft(e.target.value)}
                data-testid={`edit-${field}-input`} className={base}>
          <option value="">— Select —</option>
          {options.map((o) => <option key={o.v ?? o} value={o.v ?? o}>{o.l ?? o}</option>)}
        </select>
      );
    if (type === "list")
      return <input value={Array.isArray(draft) ? draft.join(", ") : (draft ?? "")}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Comma-separated"
                    data-testid={`edit-${field}-input`} className={base} />;
    return <input type={type === "number" ? "number" : (type === "email" ? "email" : type === "tel" ? "tel" : type === "url" ? "url" : type === "date" ? "date" : "text")}
                  value={draft ?? ""} onChange={(e) => setDraft(e.target.value)}
                  data-testid={`edit-${field}-input`} className={base} />;
  };

  return (
    <div className="border border-slate-100 rounded-md px-3 py-2 hover:bg-slate-50 group" data-testid={`field-${field}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
          <span>{label}</span>
          {source === "evaluation" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100 normal-case flex items-center gap-0.5"
                  title="Value came from latest evaluation" data-testid={`src-${field}-evaluation`}>
              <Sparkle size={9} weight="fill" /> evaluation
            </span>
          )}
          {source === "manual" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 normal-case"
                  title="Manually edited — evaluation sync will skip this field until reverted"
                  data-testid={`src-${field}-manual`}>manual</span>
          )}
        </div>
        {canEdit && !editing && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {source === "manual" && (
              <button onClick={revert} title="Revert to evaluation value"
                      data-testid={`revert-${field}`} className="text-slate-400 hover:text-sky-700">
                <ArrowCounterClockwise size={12} />
              </button>
            )}
            <button onClick={startEdit} data-testid={`edit-${field}`}
                    className="text-slate-400 hover:text-slate-700"><PencilSimple size={12} /></button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-1 flex items-center gap-2">
          {editInput()}
          <button onClick={save} disabled={saving} data-testid={`save-${field}`} className="text-emerald-700 hover:text-emerald-800 disabled:opacity-50"><Check size={16} /></button>
          <button onClick={cancel} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>
      ) : (
        <div className="text-sm text-slate-800 mt-0.5 break-words">{displayValue()}</div>
      )}
      {hint && !editing && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}
